import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Upload, Plus, Trash2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  title: "", location: "", status: "In corso", type: "",
  value: "", roi: "", mq: "", description: "",
  image_url: "", before_image_url: "",
  gallery: [], floorplan_urls: [], timeline: [],
};

async function uploadImages(files) {
  const urls = [];
  for (const file of files) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd);
    urls.push(`${BACKEND}/api/files/${data.path}`);
  }
  return urls;
}

const UploadBtn = ({ multiple = false, accept = "image/*", onUploaded, label, testid }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = await uploadImages(files);
      onUploaded(urls);
      toast.success(urls.length > 1 ? `${urls.length} file caricati.` : "File caricato.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handle}
        data-testid={`${testid}-file`}
      />
      <button
        type="button"
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase border border-amber-500/30 text-amber-400/90 px-4 py-2.5 hover:bg-amber-500/10 transition-colors duration-300 disabled:opacity-50"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
        {busy ? "Caricamento..." : label}
      </button>
    </>
  );
};

const ThumbList = ({ urls, onRemove, testid }) => (
  <div className="flex flex-wrap gap-3 mt-3">
    {urls.map((u, i) => (
      <div key={i} className="relative w-20 h-20 border border-white/10 overflow-hidden group" data-testid={`${testid}-thumb-${i}`}>
        <img src={u} alt="" className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={() => onRemove(i)}
          className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-red-400"
        >
          <Trash2 size={14} />
        </button>
      </div>
    ))}
  </div>
);

const ImageField = ({ label, value, onChange, uploadTestId, inputTestId }) => (
  <div>
    <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">{label}</p>
    <div className="flex gap-2">
      <input
        data-testid={inputTestId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://... oppure carica →"
        className="input-lux flex-1"
      />
      <UploadBtn testid={uploadTestId} label="Carica" onUploaded={(urls) => onChange(urls[0])} />
    </div>
    {value && (
      <div className="mt-2 w-24 h-16 border border-white/10 overflow-hidden">
        <img src={value} alt="" className="w-full h-full object-cover" />
      </div>
    )}
  </div>
);

export default function OperationForm({ open, operation, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (operation) {
      setForm({
        ...emptyForm,
        ...operation,
        value: String(operation.value ?? ""),
        mq: String(operation.mq ?? ""),
        gallery: operation.gallery || [],
        floorplan_urls: operation.floorplan_urls || [],
        timeline: operation.timeline || [],
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, operation]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      value: parseFloat(form.value) || 0,
      mq: parseFloat(form.mq) || 0,
      timeline: form.timeline.filter((s) => s.phase || s.date || s.description),
    };
    try {
      if (operation) await api.put(`/operations/${operation.id}`, payload);
      else await api.post("/operations", payload);
      toast.success(operation ? "Operazione aggiornata." : "Operazione creata.");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => setForm((f) => ({ ...f, timeline: [...f.timeline, { phase: "", date: "", description: "" }] }));
  const setStep = (i, k, v) =>
    setForm((f) => ({ ...f, timeline: f.timeline.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const removeStep = (i) => setForm((f) => ({ ...f, timeline: f.timeline.filter((_, j) => j !== i) }));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="bg-[#121216] border border-amber-500/15 w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-operation-form-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-semibold">
                {operation ? "Modifica Operazione" : "Nuova Operazione"}
              </h3>
              <button
                onClick={onClose}
                data-testid="admin-form-close-btn"
                className="w-9 h-9 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-[#D4AF37] hover:border-amber-500/50 transition-colors duration-300"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={save} className="space-y-7">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="form-input-title" required value={form.title} onChange={set("title")} placeholder="Titolo (IT) *" className="input-lux" />
                  <input data-testid="form-input-title-en" value={form.title_en || ""} onChange={set("title_en")} placeholder="Titolo (EN)" className="input-lux" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="form-input-location" required value={form.location} onChange={set("location")} placeholder="Location (IT) *" className="input-lux" />
                  <input data-testid="form-input-location-en" value={form.location_en || ""} onChange={set("location_en")} placeholder="Location (EN)" className="input-lux" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="form-input-type" required value={form.type} onChange={set("type")} placeholder="Tipo operazione (IT) *" className="input-lux" />
                  <input data-testid="form-input-type-en" value={form.type_en || ""} onChange={set("type_en")} placeholder="Tipo operazione (EN)" className="input-lux" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <select data-testid="form-select-status" value={form.status} onChange={set("status")} className="input-lux appearance-none">
                    <option>Conclusa</option>
                    <option>In corso</option>
                    <option>In arrivo</option>
                  </select>
                  <input data-testid="form-input-value" type="number" min="0" value={form.value} onChange={set("value")} placeholder="Valore €" className="input-lux" />
                  <input data-testid="form-input-roi" value={form.roi} onChange={set("roi")} placeholder="ROI (es. +22%)" className="input-lux" />
                  <input data-testid="form-input-mq" type="number" min="0" value={form.mq} onChange={set("mq")} placeholder="Mq" className="input-lux" />
                </div>
                <textarea data-testid="form-textarea-description" rows={4} value={form.description} onChange={set("description")} placeholder="Descrizione (IT)" className="input-lux resize-none" />
                <textarea data-testid="form-textarea-description-en" rows={4} value={form.description_en || ""} onChange={set("description_en")} placeholder="Descrizione (EN)" className="input-lux resize-none" />
                <input data-testid="form-input-listing" value={form.listing_url || ""} onChange={set("listing_url")} placeholder="Link annuncio (es. Immobiliare.it, Idealista...)" className="input-lux" />
              </div>

              <div className="border-t border-white/10 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <ImageField
                  label="Foto principale (dopo)"
                  value={form.image_url}
                  onChange={(v) => setVal("image_url", v)}
                  inputTestId="form-input-image"
                  uploadTestId="form-upload-image"
                />
                <ImageField
                  label="Foto prima (opzionale)"
                  value={form.before_image_url}
                  onChange={(v) => setVal("before_image_url", v)}
                  inputTestId="form-input-before-image"
                  uploadTestId="form-upload-before-image"
                />
              </div>

              <div className="border-t border-white/10 pt-6">
                <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Galleria foto</p>
                <UploadBtn
                  multiple
                  testid="form-upload-gallery"
                  label="Aggiungi foto"
                  onUploaded={(urls) => setVal("gallery", [...form.gallery, ...urls])}
                />
                <ThumbList
                  urls={form.gallery}
                  testid="gallery"
                  onRemove={(i) => setVal("gallery", form.gallery.filter((_, j) => j !== i))}
                />
              </div>

              <div className="border-t border-white/10 pt-6">
                <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Planimetrie</p>
                <UploadBtn
                  multiple
                  testid="form-upload-floorplans"
                  label="Aggiungi planimetrie"
                  onUploaded={(urls) => setVal("floorplan_urls", [...form.floorplan_urls, ...urls])}
                />
                <ThumbList
                  urls={form.floorplan_urls}
                  testid="floorplan"
                  onRemove={(i) => setVal("floorplan_urls", form.floorplan_urls.filter((_, j) => j !== i))}
                />
              </div>

              <div className="border-t border-white/10 pt-6">
                <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Video cantiere (MP4)</p>
                <UploadBtn
                  multiple
                  accept="video/*"
                  testid="form-upload-videos"
                  label="Aggiungi video"
                  onUploaded={(urls) => setVal("videos", [...(form.videos || []), ...urls])}
                />
                <div className="space-y-2 mt-3">
                  {(form.videos || []).map((u, i) => (
                    <div key={i} className="flex items-center gap-3 border border-white/10 px-3 py-2" data-testid={`video-row-${i}`}>
                      <span className="truncate flex-1 text-xs text-zinc-400">{u.split("/").pop()}</span>
                      <button
                        type="button"
                        onClick={() => setVal("videos", form.videos.filter((_, j) => j !== i))}
                        className="w-7 h-7 shrink-0 flex items-center justify-center border border-white/10 text-zinc-500 hover:text-red-400 hover:border-red-400/50 transition-colors duration-300"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Timeline dei lavori</p>
                  <button
                    type="button"
                    data-testid="form-add-timeline-step"
                    onClick={addStep}
                    className="flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase border border-white/15 text-zinc-300 px-3 py-2 hover:border-amber-500/50 hover:text-[#D4AF37] transition-colors duration-300"
                  >
                    <Plus size={12} /> Fase
                  </button>
                </div>
                <div className="space-y-3">
                  {form.timeline.map((step, i) => (
                    <div key={i} className="space-y-2" data-testid={`timeline-row-${i}`}>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          value={step.phase}
                          onChange={(e) => setStep(i, "phase", e.target.value)}
                          placeholder="Fase IT (es. Cantiere)"
                          className="input-lux sm:w-44"
                        />
                        <input
                          value={step.date}
                          onChange={(e) => setStep(i, "date", e.target.value)}
                          placeholder="Periodo (es. Q2 2025)"
                          className="input-lux sm:w-36"
                        />
                        <input
                          value={step.description}
                          onChange={(e) => setStep(i, "description", e.target.value)}
                          placeholder="Descrizione (IT)"
                          className="input-lux flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => removeStep(i)}
                          className="self-center w-9 h-9 shrink-0 flex items-center justify-center border border-white/10 text-zinc-500 hover:text-red-400 hover:border-red-400/50 transition-colors duration-300"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          value={step.phase_en || ""}
                          onChange={(e) => setStep(i, "phase_en", e.target.value)}
                          placeholder="Fase EN"
                          className="input-lux sm:w-44"
                        />
                        <span className="hidden sm:block sm:w-36" />
                        <input
                          value={step.description_en || ""}
                          onChange={(e) => setStep(i, "description_en", e.target.value)}
                          placeholder="Descrizione (EN)"
                          className="input-lux flex-1"
                        />
                      </div>
                    </div>
                  ))}
                  {form.timeline.length === 0 && (
                    <p className="text-xs text-zinc-600">Nessuna fase: aggiungi le fasi dell'operazione per mostrarle nella pagina dedicata.</p>
                  )}
                </div>
              </div>

              <button
                data-testid="admin-save-operation-btn"
                type="submit"
                disabled={saving}
                className="w-full bg-[#D4AF37] text-[#0A0A0C] text-xs tracking-[0.2em] uppercase font-semibold px-8 py-4 hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-50"
              >
                {saving ? "Salvataggio..." : operation ? "Salva modifiche" : "Crea operazione"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
